import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// --------------------
// Mock domaines + mock chasseur (pour mode edit)
// --------------------
const mockDomaines = [
    { id: 1, nom: "IT", reference: "IT", description: "Informatique & dev" },
    { id: 2, nom: "Finance", reference: "FIN", description: "Banque & finance" },
    { id: 3, nom: "RH", reference: "HR", description: "Ressources humaines" },
    { id: 4, nom: "Marketing", reference: "MKT", description: "Marketing & communication" },
];

const mockChasseurById = {
    10: {
        id: 10,
        nom: "Atlas Talent",
        email: "contact@atlastalent.ma",
        url: "https://atlastalent.ma",
        description: "Spécialisé IT & Fintech, sourcing et assessment.",
        domaines: [1, 2],
        picture:
            "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=480&q=60",
    },
};

// --------------------
// Styles simples (inline)
// --------------------
const styles = {
    page: { padding: 16 },
    breadcrumb: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
    crumbLink: { color: "#30478f", textDecoration: "none", borderBottom: "1px solid #30478f" },
    crumbCurrent: { color: "black", fontWeight: 700 },

    card: {
        border: "1px solid #e6e6e6",
        borderRadius: 12,
        padding: 14,
        background: "white",
    },

    row: { display: "flex", gap: 14, flexWrap: "wrap", marginTop: 14 },
    col: { flex: "1 1 320px", minWidth: 260 },

    label: { display: "block", fontWeight: 700, marginBottom: 6, color: "#2b2b2b" },
    input: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d7d7d7",
        outline: "none",
    },
    textarea: {
        width: "100%",
        minHeight: 100,
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #d7d7d7",
        outline: "none",
        resize: "vertical",
    },

    subTitle: { marginTop: 18, fontWeight: 900, color: "#111", fontSize: 14 },

    domainesGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: 12,
        marginTop: 10,
    },
    domaineCard: (checked) => ({
        border: `2px solid ${checked ? "#4466cc" : "#e5e5e5"}`,
        borderRadius: 12,
        padding: 12,
        cursor: "pointer",
        background: checked ? "rgba(68,102,204,0.08)" : "white",
        transition: "all 0.15s ease",
    }),
    domaineTitle: { fontWeight: 900, marginBottom: 4 },
    domaineDesc: { fontSize: 13, color: "#444" },
    badge: (checked) => ({
        display: "inline-block",
        marginTop: 10,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        background: checked ? "#4466cc" : "#eee",
        color: checked ? "white" : "#333",
    }),

    imagePreview: {
        marginTop: 10,
        width: 180,
        height: 120,
        objectFit: "cover",
        borderRadius: 12,
        border: "1px solid #e5e5e5",
    },

    actions: { display: "flex", justifyContent: "flex-end", marginTop: 18, gap: 10, flexWrap: "wrap" },
    btnPrimary: {
        padding: "10px 16px",
        borderRadius: 999,
        border: "none",
        background: "#4466cc",
        color: "white",
        fontWeight: 900,
        cursor: "pointer",
    },
    btnGhost: {
        padding: "10px 16px",
        borderRadius: 999,
        border: "1px solid #d7d7d7",
        background: "white",
        color: "#222",
        fontWeight: 800,
        cursor: "pointer",
    },
};

export default function AjouterChasseurDeTete() {
    const navigate = useNavigate();
    const { cid } = useParams(); // /add ou /edit/:cid

    const isEdit = Boolean(cid);

    // ⚠️ si cid ne correspond pas à un mock, on garde null (évite crash)
    const initialData = isEdit ? (mockChasseurById[cid] || null) : null;

    // Champs
    const [nom, setNom] = useState("");
    const [email, setEmail] = useState("");
    const [url, setUrl] = useState("");
    const [description, setDescription] = useState("");

    // Domaines (checked)
    const [selectedIds, setSelectedIds] = useState([]);

    // Image
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");

    // ✅ Quand cid change (add -> edit, edit -> add), on réinitialise le form
    useEffect(() => {
        setNom(initialData?.nom || "");
        setEmail(initialData?.email || "");
        setUrl(initialData?.url || "");
        setDescription(initialData?.description || "");
        setSelectedIds(initialData?.domaines || []);
        setFile(null);

        // preview = image existante en edit, sinon vide en add
        setPreview(initialData?.picture || "");
    }, [cid]); // important: cid change

    const domainesView = useMemo(() => {
        return mockDomaines.map((d) => ({ ...d, checked: selectedIds.includes(d.id) }));
    }, [selectedIds]);

    const toggleDomaine = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;

        setFile(f);

        // ✅ preview local
        const objUrl = URL.createObjectURL(f);
        setPreview(objUrl);
    };

    const canSave = nom.trim().length > 0;

    const handleSave = () => {
        // ici tu remplaceras par ton vrai dispatch plus tard
        const payload = {
            id: cid || null,
            nom,
            email,
            url,
            description,
            domaines: selectedIds,
            fileName: file?.name || null,
        };

        console.log(isEdit ? "EDIT payload:" : "ADD payload:", payload);

        // retour liste
        navigate("/recrutement/responsable/chasseurs-de-tete");
    };

    return (
        <div style={styles.page}>
            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <Link to="/" style={styles.crumbLink}>
                    Tableau de bord
                </Link>
                <span style={{ color: "#999" }}>/</span>
                <Link to="/recrutement/responsable/chasseurs-de-tete" style={styles.crumbLink}>
                    Chasseurs De Tête
                </Link>
                <span style={{ color: "#999" }}>/</span>
                <span style={styles.crumbCurrent}>
          {isEdit ? "Modifier un chasseur de tête" : "Ajouter un chasseur de tête"}
        </span>
            </div>

            {/* Form card */}
            <div style={{ ...styles.card, marginTop: 14 }}>
                <div style={styles.row}>
                    <div style={styles.col}>
                        <label style={styles.label}>Nom</label>
                        <input style={styles.input} value={nom} onChange={(e) => setNom(e.target.value)} />
                    </div>

                    <div style={styles.col}>
                        <label style={styles.label}>Email</label>
                        <input style={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                </div>

                <div style={styles.row}>
                    <div style={styles.col}>
                        <label style={styles.label}>Lien</label>
                        <input style={styles.input} value={url} onChange={(e) => setUrl(e.target.value)} />
                    </div>

                    <div style={styles.col}>
                        <label style={styles.label}>Description</label>
                        <textarea style={styles.textarea} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                </div>

                {/* Domaines */}
                <div style={styles.subTitle}>Liste des domaines à sélectionner (*)</div>

                <div style={{ marginTop: 10, padding: 12, borderRadius: 12, background: "#f7f8fb" }}>
                    <div style={styles.domainesGrid}>
                        {domainesView.map((d) => (
                            <div key={d.id} style={styles.domaineCard(d.checked)} onClick={() => toggleDomaine(d.id)}>
                                <div style={styles.domaineTitle}>
                                    {d.nom} <span style={{ color: "#888", fontWeight: 700 }}>({d.reference})</span>
                                </div>
                                <div style={styles.domaineDesc}>{d.description}</div>
                                <span style={styles.badge(d.checked)}>{d.checked ? "Sélectionné" : "Non sélectionné"}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Image upload */}
                <div style={{ marginTop: 16 }}>
                    <label style={{ ...styles.label, color: "#30478f" }}>Image :</label>

                    {preview ? (
                        <img src={preview} alt="preview" style={styles.imagePreview} />
                    ) : (
                        <div style={{ fontSize: 13, color: "#666" }}>Aucune image sélectionnée</div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <input type="file" accept="image/*" onChange={handleFileChange} />
                    </div>
                </div>

                {/* Actions */}
                <div style={styles.actions}>
                    <button
                        type="button"
                        style={styles.btnGhost}
                        onClick={() => navigate("/recrutement/responsable/chasseurs-de-tete")}
                    >
                        Annuler
                    </button>

                    <button
                        type="button"
                        style={{ ...styles.btnPrimary, opacity: canSave ? 1 : 0.6 }}
                        onClick={handleSave}
                        disabled={!canSave}
                        title={!canSave ? "Le nom est obligatoire" : "Enregistrer"}
                    >
                        Enregistrer
                    </button>
                </div>

                {/* Optionnel: aide debug si cid n'existe pas */}
                {isEdit && !initialData && (
                    <div style={{ marginTop: 12, color: "#b00020", fontWeight: 800 }}>
                        ⚠️ Aucun mock trouvé pour cid={cid}. (C’est normal si tu relies plus tard aux vraies données)
                    </div>
                )}
            </div>
        </div>
    );
}
